import {
  $InternalStudentsState,
  $InternalStudents,
  fetchInternalStudents,
} from "@store/internal-study/students-store";
import { StudentShortDataT } from "@interfaces/internal-study/students";
import InternalStudyStudentsService from "@services/internal-study-students-service";
import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import { Checkbox, Tooltip } from "@mui/material";
import { ErrorIndicator, Loader } from "@ui/indicators";
import { NotePencilIcon, TrashIcon } from "@phosphor-icons/react";
import React, { useEffect } from "react";

import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import clsx from "clsx";
import style from "@scss/pages/company/departments.module.scss";
import tableStyle from "@scss/components/tables/base-table.module.scss";
import { useAsyncConfirm } from "@hooks/useAsyncConfirm";
import { useChoiceState } from "@hooks/useChoiceState";
import { useCompanyId } from "@store/company/company-store";
import useModal from "@modals/modal-hook";
import { useUnit } from "effector-react";

const InternalStudyStudentsTable: React.FC = () => {
  const { isLoading, error, isFetched } = useUnit($InternalStudentsState);
  const internalStudents = useUnit($InternalStudents);
  const permissions = useUnit($UserAddPermissions);
  const companyId = useCompanyId();

  const {
    selectedItems,
    areAllItemsSelected,
    selectAll,
    selectById,
    clearSelectedItems,
    getIsItemSelected,
  } = useChoiceState(internalStudents, (students) => students.id);

  const { open } = useModal();
  const confirm = useAsyncConfirm();

  const handleRemoveStudent = (student: StudentShortDataT) => {
    open("ConfirmActionModal", {
      btnText: "Удалить",
      modalData: {
        text: `Вы уверены, что хотите удалить аккаунт обучающегося "${student.employee_info.name}"?`,
      },
      onConfirm: () => {
        InternalStudyStudentsService.delete(student.id, (err, res) => {
          if (err || !res) {
            return Error("При удалении обучающегося произошла ошибка");
          }
          if (res.data && companyId !== null) {
            fetchInternalStudents(companyId);
          }
        });
      },
    });
  };

  const handleDeleteSelectedStudents = async () => {
    const ok = await confirm({
      modalData: {
        text: "Вы уверены, что хотите удалить выбранных обучающихся?",
      },
    });
    if (!ok) return;

    for (const student of selectedItems) {
      InternalStudyStudentsService.delete(student.id, (err, res) => {});
    }

    clearSelectedItems();
    if (companyId !== null) {
      fetchInternalStudents(companyId);
    }
  };

  useEffect(() => {
    if (!isFetched && companyId !== null) {
      fetchInternalStudents(companyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={clsx(
        tableStyle.base_table_container,
        style.departments_table,
        style.no_border_top
      )}
    >
      {isLoading ? (
        <Loader />
      ) : error ? (
        <ErrorIndicator />
      ) : (
        <table className={clsx(tableStyle.base_table)}>
          <thead>
            <tr>
              <td>
                <span>
                  <Checkbox
                    color="primary"
                    checked={areAllItemsSelected}
                    onChange={(e) => selectAll(e.target.checked)}
                  />
                </span>
                <label className={clsx(tableStyle.checkbox_label)}>Имя</label>
              </td>
              <td align="left">Логин</td>
              <td align="right">
                {selectedItems.length > 0 && (
                  <span>
                    {permissions.hasPermission(
                      ACTIONS.educationalCenter_internalTraining_student_allowedToDelete
                    ) && (
                      <Tooltip title="Удалить" placement="top">
                        <TrashIcon
                          size={24}
                          className={style.department_actions_col__icon}
                          onClick={handleDeleteSelectedStudents}
                        />
                      </Tooltip>
                    )}
                  </span>
                )}
              </td>
            </tr>
          </thead>
          <tbody>
            {internalStudents.length ? (
              internalStudents.map((student) => (
                <tr key={student.id}>
                  <td align="left">
                    <span>
                      <Checkbox
                        color="primary"
                        checked={getIsItemSelected(student)}
                        onChange={(e) => selectById(student, e.target.checked)}
                      />
                    </span>
                    <span className={style.wrapperRow}>
                      <label
                        className={clsx(
                          tableStyle.column_fixed_height,
                          style.wrapperLabel
                        )}
                        onClick={() =>
                          open("EditInternalStudentModal", {
                            btnText: "Сохранить",
                            modalData: {
                              id: student.id,
                              title: student.employee_info.name,
                            },
                          })
                        }
                      >
                        <span className={style.wrapperLabel}>
                          {student.employee_info.name}
                        </span>
                      </label>
                    </span>
                  </td>
                  <td align="left">{student.login}</td>
                  <td>
                    <div className={clsx(style.department_actions_col)}>
                      {permissions.hasPermission(
                        ACTIONS.educationalCenter_internalTraining_student_allowedToEdit
                      ) && (
                        <WrapperSVG color={"orange"}>
                          <Tooltip title="Изменить" placement="top">
                            <NotePencilIcon
                              size={24}
                              className={style.department_actions_col__icon}
                              onClick={() =>
                                open("EditInternalStudentModal", {
                                  btnText: "Сохранить",
                                  modalData: {
                                    id: student.id,
                                    title: student.employee_info.name,
                                  },
                                })
                              }
                            />
                          </Tooltip>
                        </WrapperSVG>
                      )}
                      {permissions.hasPermission(
                        ACTIONS.educationalCenter_internalTraining_student_allowedToDelete
                      ) && (
                        <Tooltip title="Удалить" placement="top">
                          <TrashIcon
                            size={24}
                            className={style.department_actions_col__icon}
                            onClick={() => handleRemoveStudent(student)}
                          />
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td>
                  <p
                    style={{
                      textAlign: "center",
                      width: "100%",
                      margin: "24px 0px",
                    }}
                  >
                    Обучающиеся не добавлены
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default InternalStudyStudentsTable;
